// Offline tab: TV first, then OOH / radio / print.
//
// Two blocks rather than one, because TV is measured differently. A GRP is a rating, not a
// count, so it is never summed into a series beside impressions and never calibrated —
// calibration adjusts spend, and an audience measurement is not an estimate of spend. Only
// the money columns move between the Calibrated and Raw settings.
//
// Offline rows live in cm_statex_data alongside the digital ones, separated by the generated
// `media_type` column. Nothing here can show a digital row and nothing in the Digital tab can
// show one of these.

import { useState, useEffect, useMemo } from 'react';
import { cmApi } from '../../api.js';
import { useClient } from '../../ClientContext.jsx';
import { fmtCurrency, fmtNumber } from '../../format.js';
import DonutChart from '../DonutChart.jsx';
import StackedBarChart from '../StackedBarChart.jsx';
import { Band, Panel, inkAlpha } from './Band.jsx';
import { pivot } from './pivot.js';
import { T, SERIES as PALETTE } from '../../../../shared/styles/tokens.js';

const monthLabel = (m) => (m
  ? new Date(m).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
  : '—');


/**
 * The TV table as the reference prints it: category, channel and programme appear once, then
 * a row per brand, promotion status and spot duration beneath.
 *
 * Repeats are blanked by comparing against the previous row rather than by grouping into a
 * tree — the rows arrive already ordered by spend, and regrouping them would reorder the
 * table away from the ranking that decides what is at the top.
 */
function TvTable({ rows }) {
  if (!rows?.length) return <p className="o-state">No TV rows for this selection.</p>;
  const max = Math.max(...rows.map(r => Number(r.spend) || 0), 1);
  const same = (a, b, k) => a && b && a[k] === b[k];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="o-tbl">
        <thead>
          <tr>
            <th>Category</th><th>TV channel</th><th>Programme</th><th>Brand</th>
            <th>Promotion status</th>
            <th style={{ textAlign: 'right' }}>Spot duration</th>
            <th style={{ textAlign: 'right' }}>TV GRPs</th>
            <th style={{ textAlign: 'right' }}>Spend</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const p = rows[i - 1];
            const sameProg = same(p, r, 'category_name') && same(p, r, 'tv_channel')
                          && same(p, r, 'programme');
            return (
              <tr key={i}>
                <td className="o-strong">{sameProg ? '' : (r.category_name || '—')}</td>
                <td>{sameProg ? '' : (r.tv_channel || '—')}</td>
                <td>{sameProg ? '' : (r.programme || '—')}</td>
                <td>{same(p, r, 'brand_name') && sameProg ? '' : (r.brand_name || '—')}</td>
                <td style={{ color: T.text3 }}>{r.promotion_status || '—'}</td>
                <td className="o-mono" style={{ textAlign: 'right' }}>
                  {r.spot_duration == null ? '—' : fmtNumber(r.spot_duration)}
                </td>
                <td className="o-mono" style={{ textAlign: 'right' }}>
                  {r.grp == null ? '—' : fmtNumber(r.grp)}
                </td>
                <td className="o-mono" style={{
                  textAlign: 'right',
                  background: inkAlpha(0.04 + 0.14 * ((Number(r.spend) || 0) / max)),
                }}>{fmtCurrency(r.spend)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OtherTable({ rows }) {
  if (!rows?.length) return <p className="o-state">No OOH, radio or print rows for this selection.</p>;
  const max = Math.max(...rows.map(r => Number(r.spend) || 0), 1);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="o-tbl">
        <thead>
          <tr>
            <th>Brand</th><th>Medium</th><th>Sub-medium type</th>
            <th>Programme</th><th>Programme sub-type</th><th>Spot</th>
            <th style={{ textAlign: 'right' }}>Spend (USD)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="o-strong">{r.brand_name || '—'}</td>
              <td>{r.medium || '—'}</td>
              <td>{r.sub_medium_type || '—'}</td>
              <td>{r.programme || '—'}</td>
              <td>{r.programme_sub_type || '—'}</td>
              <td>{r.spot || '—'}</td>
              <td className="o-mono" style={{
                textAlign: 'right',
                background: inkAlpha(0.04 + 0.14 * ((Number(r.spend) || 0) / max)),
              }}>{fmtCurrency(r.spend)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function OfflineOverview({ filters, calibrated }) {
  const { clientId } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true); setErr(null);
    const params = Object.fromEntries(
      Object.entries(filters || {}).filter(([, v]) => v));
    cmApi.getOffline(clientId, { ...params, calibrated: calibrated ? 'true' : 'false' })
      .then(setData)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [clientId, filters, calibrated]);

  // One colour per brand across every panel on the tab. Indexing the palette per chart is
  // how a brand ends up gold in the donut and purple in the bars beside it.
  const colors = useMemo(() => {
    const map = {};
    let i = 0;
    for (const r of [...(data?.tv?.share || []), ...(data?.other?.by_brand || [])]) {
      if (!map[r.label]) map[r.label] = PALETTE[i++ % PALETTE.length];
    }
    for (const r of data?.tv?.monthly || []) {
      if (!map[r.k]) map[r.k] = PALETTE[i++ % PALETTE.length];
    }
    return map;
  }, [data]);

  const mediumColors = useMemo(() => {
    const map = {};
    (data?.other?.by_medium || []).forEach((r, i) => {
      map[r.label] = PALETTE[i % PALETTE.length];
    });
    return map;
  }, [data]);

  const grpMonthly = useMemo(
    () => pivot(data?.tv?.monthly, 'bucket', 'k', 'grp', monthLabel), [data]);
  const tvSpendMonthly = useMemo(
    () => pivot(data?.tv?.monthly, 'bucket', 'k', 'spend', monthLabel), [data]);

  if (!clientId) return <p className="o-state">Select a client to view offline activity.</p>;
  if (loading) return <p className="o-state">Loading…</p>;
  if (err) return <p className="o-state o-error">{err}</p>;

  if (!data?.has_data) {
    return (
      <p className="o-hint">
        <b>No offline rows loaded.</b> The Statex export carries offline activity in the same
        file as digital; a row counts as offline once it has a medium, a TV channel or a GRP.
        Nothing imported for this client has one yet. Import an export containing those
        columns on the Import Data tab and this fills in.
      </p>
    );
  }

  const tv = data.tv || {};
  const other = data.other || {};

  return (
    <>
      <p className="o-note" style={{ marginBottom: T.s5 }}>
        Spend is {data.calibration_basis === 'mixed' ? 'a mix of calibrated and raw Statex' : data.calibrated ? 'calibrated' : 'raw Statex (unadjusted)'}. GRPs are never calibrated —
        a rating is an audience measurement, not an estimate of spend.
      </p>

      <Band title="Offline Overview (TV)" sub="Share and monthly trend, by brand">
        <div className="cm-chart-pair" style={{ marginBottom: T.s4 }}>
          <Panel title="Share of TV GRPs">
            <DonutChart segments={(tv.share || []).map(r => ({
              label: r.label, value: Number(r.grp || 0), color: colors[r.label],
            }))} />
          </Panel>
          <Panel title="Monthly TV GRPs by brand">
            <StackedBarChart data={grpMonthly.rows} series={grpMonthly.keys}
              colors={colors} format="number" height={170} />
          </Panel>
        </div>
        <div className="cm-chart-pair" style={{ marginBottom: T.s4 }}>
          <Panel title="Share of spend (offline TV)">
            <DonutChart format="currency" segments={(tv.share || []).map(r => ({
              label: r.label, value: Number(r.spend || 0), color: colors[r.label],
            }))} />
          </Panel>
          <Panel title="Monthly spend by brand (offline TV)">
            <StackedBarChart data={tvSpendMonthly.rows} series={tvSpendMonthly.keys}
              colors={colors} format="currency" height={170} />
          </Panel>
        </div>
        <Panel><TvTable rows={tv.detail} /></Panel>
      </Band>

      <Band title="Offline Overview (OOH, Radio, Print)" sub="Share of offline spend">
        <div className="cm-chart-pair cm-chart-pair-equal" style={{ marginBottom: T.s4 }}>
          <Panel title="Share of offline spend by brand">
            <DonutChart format="currency" segments={(other.by_brand || []).map(r => ({
              label: r.label, value: Number(r.spend || 0), color: colors[r.label],
            }))} />
          </Panel>
          <Panel title="Share of offline spend by channel">
            <DonutChart format="currency" segments={(other.by_medium || []).map(r => ({
              label: r.label, value: Number(r.spend || 0), color: mediumColors[r.label],
            }))} />
          </Panel>
        </div>
        <Panel><OtherTable rows={other.detail} /></Panel>
      </Band>
    </>
  );
}
